import type { ReportRow } from '../lib/types'

export type ColumnAlign = 'left' | 'right'
export type ColumnFormat = 'integer' | 'decimal' | 'percent' | 'currency' | 'timestamp' | 'boolean' | 'text'

export interface ColumnMeta {
  align?: ColumnAlign
  format?: ColumnFormat
  sticky?: boolean
  hidden?: boolean
  label?: string
}

const STICKY_COLUMNS = new Set([
  'vehicle_id', 'driver_id', 'driver_name', 'day', 'month', 'hub_code', 'kind', 'issue', 'place',
])

const HIDDEN_BY_DEFAULT = new Set(['_seen', 'internal_id'])

const LOAD_STATE_COLUMNS = new Set(['load_state', 'laden'])

const COLUMN_LABELS: Record<string, string> = {
  load_state: 'Load',
  laden: 'Load',
  event_ts: 'Time',
  vehicle_id: 'Vehicle',
  message: 'Detail',
  place: 'Hub / place',
  driver_name: 'Driver',
}

/**
 * Map backend LADEN/EMPTY (and ghat `laden` booleans) to operator-facing labels.
 *
 * LADEN / EMPTY / UNLADEN are unambiguous tokens and may appear under any column
 * name. Boolean-ish values (true/false, 0/1, t/f) are NOT — a bus report cell with
 * n=1 must stay "1", not "Loaded". Those forms only rewrite on known load columns.
 */
export function formatLoadStateDisplay(v: unknown, col?: string): string | null {
  if (v == null) return null
  const isLoadCol = !!col && LOAD_STATE_COLUMNS.has(col)

  if (typeof v === 'boolean') {
    if (!isLoadCol) return null
    return v ? 'Loaded' : 'Unloaded'
  }
  // Numeric 0/1 used to stringify to "0"/"1" and hit the ambiguous branch below,
  // so MBMT counts (n=1, Min=1, Critical=1) rendered as truck "Loaded"/"Unloaded".
  if (typeof v === 'number') {
    if (!isLoadCol) return null
    if (v === 1) return 'Loaded'
    if (v === 0) return 'Unloaded'
    return null
  }

  const s = String(v).trim()
  if (!s) return null

  if (/^laden$/i.test(s)) return 'Loaded'
  if (/^empty$/i.test(s) || /^unladen$/i.test(s)) return 'Unloaded'

  if (!isLoadCol) return null
  if (/^true$/i.test(s) || s === 't' || s === '1') return 'Loaded'
  if (/^false$/i.test(s) || s === 'f' || s === '0') return 'Unloaded'
  if (/^unknown$/i.test(s)) return 'Unknown'
  return s
}

/** The one place that turns a load enum into the words an operator reads. */
export function loadWord(v: unknown): string {
  return formatLoadStateDisplay(v, 'load_state') ?? 'Unknown'
}

function inferFormat(col: string): ColumnFormat {
  if (col.includes('_pct') || col.includes('pct') || col === 'score') return 'percent'
  if (col.includes('_inr') || col.includes('cost') || col.includes('revenue') || col.includes('contribution')) return 'currency'
  if (col.includes('_ts') || col.endsWith('_at') || col === 'ts') return 'timestamp'
  if (col.startsWith('is_') || col === 'breached' || col === 'acknowledged') return 'boolean'
  return 'text'
}

function inferAlign(col: string, format: ColumnFormat, sample?: unknown): ColumnAlign {
  if (format === 'currency' || format === 'percent' || format === 'integer' || format === 'decimal') return 'right'
  if (typeof sample === 'number') return 'right'
  if (col.includes('_id') || col.includes('_count') || col.includes('_min') || col.includes('_km')) return 'right'
  return 'left'
}

export function getColumnMeta(col: string, sample?: unknown): ColumnMeta {
  const format = inferFormat(col)
  return {
    align: inferAlign(col, format, sample),
    format,
    sticky: STICKY_COLUMNS.has(col),
    hidden: HIDDEN_BY_DEFAULT.has(col),
    label: COLUMN_LABELS[col] ?? col.replace(/_/g, ' '),
  }
}

/** First identifier column to pin while scrolling horizontally. */
export function firstStickyColumn(
  columns: string[],
  columnDefs?: Record<string, { sticky?: boolean }>,
): string | null {
  if (!columns.length) return null
  const marked = columns.find((col) => columnDefs?.[col]?.sticky ?? getColumnMeta(col).sticky)
  return marked ?? columns[0]!
}

export function buildColumns(rows: ReportRow[]): string[] {
  if (!rows.length) return []
  return Object.keys(rows[0]!)
}

export function formatCellValue(
  v: unknown,
  col: string,
  meta?: ColumnMeta,
  opts?: { applyLoadStateLabels?: boolean },
): string {
  if (opts?.applyLoadStateLabels !== false) {
    const loadLabel = formatLoadStateDisplay(v, col)
    if (loadLabel != null) return loadLabel
  }

  const format = meta?.format ?? inferFormat(col)
  if (v == null) return '—'
  if (format === 'boolean') {
    if (typeof v === 'boolean') return v ? 'yes' : 'no'
    return truthy(v) ? 'yes' : 'no'
  }
  if (typeof v === 'number') {
    if (format === 'percent') return v.toFixed(1)
    if (format === 'currency') return Number.isInteger(v) ? String(v) : v.toFixed(2)
    if (format === 'integer') return String(Math.round(v))
    if (format === 'decimal') return Number.isInteger(v) ? String(v) : v.toFixed(2)
    return Number.isInteger(v) ? String(v) : v.toFixed(2)
  }
  return String(v)
}

export function formatCellDisplay(
  v: unknown,
  col: string,
  meta?: ColumnMeta,
  opts?: { applyLoadStateLabels?: boolean },
): string {
  const s = formatCellValue(v, col, meta, opts)
  const format = meta?.format ?? inferFormat(col)
  if (format === 'currency' && s !== '—') return `₹${s}`
  if (format === 'percent' && s !== '—') return `${s}%`
  return s
}

export function compareValues(a: unknown, b: unknown, _col?: string): number {
  const na = num(a)
  const nb = num(b)
  if (na != null && nb != null) return na - nb
  const sa = a == null ? '' : String(a)
  const sb = b == null ? '' : String(b)
  return sa.localeCompare(sb, undefined, { numeric: true })
}

export function rowMatchesFilter(row: ReportRow, query: string, columns: string[]): boolean {
  if (!query.trim()) return true
  const q = query.toLowerCase()
  return columns.some((c) => {
    const v = row[c]
    if (v == null) return false
    return String(v).toLowerCase().includes(q)
  })
}

export function rowIsAccent(row: ReportRow, reportId?: string): boolean {
  if (reportId === 'tat_breach') return truthy(row.breached)
  return false
}

function num(v: unknown): number | null {
  if (typeof v === 'number' && Number.isFinite(v)) return v
  if (typeof v === 'string' && v.trim() !== '' && Number.isFinite(Number(v))) return Number(v)
  return null
}

function truthy(v: unknown): boolean {
  return v === true || v === 't' || v === 'true' || v === 1
}
